import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/clients';

export interface Notification {
  id: string;
  type: 'stock' | 'comprobante' | 'cuota' | 'info' | 'success' | 'warning' | 'recordatorio';
  title: string;
  message: string;
  link?: string;
  read: boolean;
  timestamp: Date;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isMounted = useRef(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadNotifications = useCallback(async (silent: boolean = false) => {
    if (!isMounted.current) return;
    try {
      if (!silent) setLoading(true);
      else setIsRefreshing(true);

      const response = await api.get('/notifications');
      const data = response.data.data || [];
      const parsed = data.map((n: any) => ({ ...n, timestamp: new Date(n.timestamp) }));
      
      const currentIds = notifications.map(n => n.id).sort().join(',');
      const newIds = parsed.map((n: any) => n.id).sort().join(',');
      
      if (currentIds !== newIds || notifications.length !== parsed.length) {
        setNotifications(parsed);
      }
      
      const newUnreadCount = response.data.unread || parsed.filter((n: any) => !n.read).length;
      setUnreadCount(prev => prev !== newUnreadCount ? newUnreadCount : prev);
      setError(null);
    } catch (err: any) {
      if (err.response?.status !== 429) {
        setError(err.message || 'Error al cargar notificaciones');
      }
    } finally {
      if (!silent) setLoading(false);
      else setIsRefreshing(false);
    }
  }, [notifications]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Error silencioso
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Error silencioso
    }
  }, []);

  const refresh = useCallback(() => loadNotifications(false), [loadNotifications]);

  useEffect(() => {
    isMounted.current = true;
    loadNotifications(false);
    intervalRef.current = setInterval(() => loadNotifications(true), 60000);
    return () => {
      isMounted.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    isRefreshing,
    markAllAsRead,
    markAsRead,
    refresh,
  };
}